import { Session, useAuth, ValidatedSessions, isAuthFailure } from '@ais-ui/auth';
import { LoadingSpinner, useToast } from '@ais-ui/ui-core';
import * as Sentry from '@sentry/react';

import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { periodicCheck } from './ol-server/checkSearchAndRescue';
import { checkSessionValidWithTimeout } from './ol-server/checkSessionValid';
import { closeAllPopups } from '@ais-ui/popups';


const SessionContext = createContext<ValidatedSessions | null>(null);

export interface SessionProviderProps {
  children: ReactNode;
  logout?: (reason: string) => void;
  reconnect?: (server: string) => void;
}

export const SessionProvider = ({
  children,
  logout,
  reconnect,
}: SessionProviderProps) => {
  const { sessions, setSessions, credentials } = useAuth();
  const { toast } = useToast();

  // Track if we're currently validating to prevent multiple validations
  const isValidatingRef = useRef(false);

  useEffect(() => {
    const performLogout = (reason: string) => {
      if (logout) {
        closeAllPopups();
        logout(reason);
      }
    };

    const handleSessionError = async (event: Event) => {
      const customEvent = event as CustomEvent<{
        errorMessage: string;
        isNewLogon: boolean;
        isSessionTimeout: boolean;
      }>;
      const detail = customEvent?.detail;
      const errorMessage = detail?.errorMessage || '';

      // Auth-related errors: logout immediately, no point trying to reconnect
      if (detail?.isNewLogon) {
        performLogout('New login detected. You have been logged out.');
        return;
      }
      if (detail?.isSessionTimeout) {
        performLogout('Your session has timed out.');
        return;
      }

      // Definite auth failures: logout immediately
      if (isAuthFailure(errorMessage)) {
        Sentry.captureMessage(`Session auth failure: ${errorMessage}`, {
          level: 'info',
          tags: {
            error_severity: 'auth_failure',
            error_type: 'session_invalid',
          },
        });
        performLogout('Your session has expired.');
        return;
      }

      // For connectivity issues, check if we can validate or need to reconnect
      if (isValidatingRef.current) {
        return; // Already validating, don't start another
      }

      // Check if WebSocket is still open - if so, we can try to validate
      const wsState = sessions?.olSession?.webSocket?.readyState;
      const isWebSocketOpen = wsState === WebSocket.OPEN;

      if (isWebSocketOpen && sessions?.olSession && sessions?.sessionId && credentials?.username) {
        // WebSocket is still open - validate the session
        isValidatingRef.current = true;

        const { dismiss } = toast({
          title: 'Checking connection...',
          description: 'Verifying your session is still active.',
        });

        try {
          const result = await checkSessionValidWithTimeout(
            sessions.olSession,
            sessions.sessionId,
            credentials.username,
            8000 // 8 second timeout
          );

          dismiss();

          if (result.valid) {
            // Session is still valid - this was just a transient error
            Sentry.captureMessage('Session error recovered - session still valid', {
              level: 'warning',
              tags: {
                error_severity: 'connectivity',
                error_type: 'session_error_recovered',
              },
              extra: { originalError: errorMessage },
            });

            toast({
              title: 'Connection restored',
              description: 'Your session is still active.',
            });
          } else {
            // Session is not valid
            Sentry.captureMessage('Session validation failed: invalid', {
              level: 'info',
              tags: {
                error_severity: 'auth_failure',
                error_type: 'session_invalid',
              },
              extra: { originalError: errorMessage, validationError: result.error },
            });
            performLogout('Your session has expired.');
          }
        } catch (error) {
          dismiss();

          // Validation request failed - treat as connectivity issue, try reconnect
          Sentry.captureMessage('Session validation request failed', {
            level: 'warning',
            tags: {
              error_severity: 'connectivity',
              error_type: 'validation_request_failed',
            },
            extra: {
              originalError: errorMessage,
              validationError: error instanceof Error ? error.message : 'Unknown error',
            },
          });

          if (reconnect) {
            toast({
              title: 'Connection issue',
              description: 'Attempting to reconnect...',
            });
            reconnect('olSession');
          }
        }

        isValidatingRef.current = false;
        return;
      }

      // WebSocket is closed - let connectionClosed event handler trigger reconnection
      Sentry.captureMessage('Session error with closed WebSocket', {
        level: 'warning',
        tags: {
          error_severity: 'connectivity',
          error_type: 'websocket_closed_on_error',
        },
        extra: { originalError: errorMessage, wsState },
      });
    };

    window.addEventListener('sessionError', handleSessionError);
    return () => window.removeEventListener('sessionError', handleSessionError);
  }, [logout, sessions, credentials, toast, reconnect]);

  // Ensure all popups are closed when the main window/tab is closed or reloaded
  useEffect(() => {
    const handleBeforeUnload = () => {
      try {
        closeAllPopups();
      } catch (e) {
        console.error('Failed to close popups on unload:', e);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    // pagehide is more reliable on some mobile browsers
    window.addEventListener('pagehide', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    const handleConnectionClosed = async (event: Event) => {
      // Access the detail argument that tells us which session was closed
      const customEvent = event as CustomEvent<string>;
      const sessionType = customEvent?.detail;

      // We set the sessionType to undefined to indicate that the session is closed
      setSessions((prev) => ({
        ...prev,
        [sessionType]: undefined,
      }));

      if (reconnect) {
        reconnect(sessionType);
      }
    };

    window.addEventListener('connectionClosed', handleConnectionClosed);
    return () => {
      window.removeEventListener('connectionClosed', handleConnectionClosed);
    };
  }, [reconnect, setSessions]);

  // Keep-alive effect, this makes sure that the olserver does not become idle, and thus
  //  the user can turn away from their computer without getting logged out
  useEffect(() => {
    // Only set up keep-alive if we have a valid olSession
    if (!sessions?.olSession) return;

    const doPeriodicCheck = async () => {
      try {
        await periodicCheck(sessions.olSession as Session);
      } catch (error) {
        console.error('Keep-alive check failed:', error);
      }
    };

    doPeriodicCheck();

    // Set up interval for subsequent checks
    const interval = setInterval(doPeriodicCheck, 15000);

    return () => clearInterval(interval);
  }, [sessions?.olSession]);

  // Handle tab visibility changes - mobile browsers aggressively suspend/close WebSocket
  // connections when tabs are hidden to save battery. When the tab becomes visible again,
  // check each WebSocket and reconnect if needed.
  useEffect(() => {
    if (!reconnect) return;

    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        const sessionTypes = ['olSession', 'dbSession', 'replaySession'] as const;

        for (const sessionType of sessionTypes) {
          const session = sessions?.[sessionType];
          if (session?.webSocket) {
            const wsState = session.webSocket.readyState;
            // WebSocket.CLOSED = 3, WebSocket.CLOSING = 2
            if (wsState === WebSocket.CLOSED || wsState === WebSocket.CLOSING) {
              console.log(`${sessionType} WebSocket disconnected while tab was hidden, reconnecting...`);
              reconnect(sessionType);
            }
          }
        }

        // Also trigger a keep-alive check to validate session is still valid server-side
        if (sessions?.olSession?.webSocket?.readyState === WebSocket.OPEN) {
          try {
            await periodicCheck(sessions.olSession as Session);
          } catch (error) {
            console.error('Keep-alive check failed after visibility change:', error);
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [reconnect, sessions]);

  const value = useMemo(() => {
    if (!sessions?.olSession) return null;
    return sessions as ValidatedSessions;
  }, [sessions]);

  const content = value ? children : <LoadingSpinner />;

  return (
    <SessionContext.Provider value={value}>{content}</SessionContext.Provider>
  );
};

export const useSession = () => {
  const sessionContext = useContext(SessionContext);

  if (!sessionContext) {
    throw new Error('useSession must be used within a SessionProvider');
  }

  return sessionContext;
};

/**
 * Lightweight session context provider for popup windows or other contexts
 * where sessions are already established and don't need the full SessionProvider logic.
 * 
 * This allows reusing hooks like useCompleteReportExportFetchingProcess in popups.
 */
export interface SessionContextProviderProps {
  children: ReactNode;
  sessions: ValidatedSessions;
}

export const SessionContextProvider = ({
  children,
  sessions,
}: SessionContextProviderProps) => {
  return (
    <SessionContext.Provider value={sessions}>
      {children}
    </SessionContext.Provider>
  );
};
